import { createPortal } from "react-dom"
import { useRef, SyntheticEvent } from "react"

// ** Third Party Components
import { useNavigate } from "react-router-dom"
import { useVirtualizer } from "@tanstack/react-virtual"
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  pointerWithin,
} from "@dnd-kit/core"

// ** UI Components
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/common/hover-card"

// ** Types
import { DictType } from "@pma/types"

// ** Helpers
import { cn, getFileDisplayType } from "@pma/utils/helpers"

// ** Routes
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"

// ** Custom Components
import RenderFileIcon from "./render-file-icon"
import { FilePreview } from "@components/dashboard/preview-file"

const COLUMN_COUNT = 4
const ESTIMATED_ROW_HEIGHT = 160

interface VirtualGridViewProps {
  showAsModal?: boolean
}

export default function VirtualGridView({ showAsModal = false }: VirtualGridViewProps) {
  const { translate } = useTranslator()
  const navigate = useNavigate()
  const {
    activeFolder,
    folderItems,
    batchData,
    setBatchData,
    showBatchSelection,
    filterActive,
    setClearFilter,
    setNewActiveData,
    setFolderItems,
    handleTreeItemFolderClick,
    refetch,
    isLoading,
    isFetching,
    isFetched,
    nextPage,
    handleLoadMoreData,
    project,
  } = useFilesView()

  const items: DictType[] = folderItems[activeFolder?.id] ?? []
  const parentRef = useRef<HTMLDivElement>(null)

  // Calculate rows for virtualization
  const rowCount = Math.ceil(items.length / COLUMN_COUNT)

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 3,
  })

  // DnD sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const [activeFileInfinite, setActiveFileInfinite] = useState<DictType | null>(null)
  const [hoveredFolderId, setHoveredFolderId] = useState<string | null>(null)

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === "File") {
      setActiveFileInfinite(event.active.data.current.file)
      setHoveredFolderId(null)
    }
  }

  function onDragOver(event: any) {
    const { over } = event
    if (!over) {
      setHoveredFolderId(null)
      return
    }
    const overId = over.id.toString()
    setHoveredFolderId(overId.startsWith("folder_") ? overId : null)
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event
    setActiveFileInfinite(null)
    setHoveredFolderId(null)
    if (!over || active.id === over.id) return

    const activeId = active.id.toString()
    const activeFolderId = active?.data?.current?.file?.folders_id
    const overId = over.id.toString()

    if (activeId.startsWith("file_infinite_") && overId.startsWith("folder_")) {
      const fileId = parseInt(activeId.split("_")[2])
      const folderId = parseInt(overId.split("_")[1])
      const file = items.find((i) => i.id === fileId && i.as === "file")
      const targetFolder = items.find((i) => i.id === folderId && i.as === "folder")

      if (file && targetFolder) {
        // Optimistic update
        if (folderItems[folderId] !== undefined) {
          setFolderItems((prev) => ({
            ...prev,
            [folderId]: [...prev[folderId], file],
            [activeFolderId]: prev[activeFolderId].filter((f) => f.id !== fileId),
          }))
        } else {
          setFolderItems((prev) => ({
            ...prev,
            [activeFolderId]: prev[activeFolderId].filter((f) => f.id !== fileId),
          }))
        }
        // The moveFileMutation would be called via context - simplified here
      }
    }
  }

  const handleClick = (item: any) => {
    if (item) {
      document.getElementById(`check-${item.id}`)?.click()
    }
  }

  const handleSelection = (selected: DictType, event: SyntheticEvent) => {
    const { checked } = event.target as HTMLInputElement
    if (checked) {
      setBatchData([selected, ...batchData])
    } else {
      setBatchData(batchData.filter((val: any) => val.id !== selected.id))
    }
  }

  const handleShowTask = (item: any) => {
    if (item?.task_id !== 0) {
      return (
        <div
          className='mt-1 cursor-pointer rounded-full bg-blue-100 px-2 py-1 text-[10px] font-bold'
          onClick={() => navigate(`${PROJECTS_PAGE_ROUTE}/${item.project_id}`)}
        >
          PR-{item.task_id}
        </div>
      )
    }
    if (item?.diary_id !== 0) {
      return (
        <div
          className='mt-1 cursor-pointer rounded-full bg-blue-100 px-2 py-1 text-[10px] font-bold'
          onClick={() => navigate(`${PROJECTS_PAGE_ROUTE}/${item.project_id}`)}
        >
          WD-{item.diary_id}
        </div>
      )
    }
    return null
  }

  const containerHeight =
    showAsModal && window?.innerWidth > 1024
      ? "calc(100% - 6rem)"
      : window?.innerWidth < 425
        ? "calc(100% - 15rem)"
        : "calc(100% - 10rem)"

  // Loading state
  if ((isLoading || isFetching) && items.length === 0) {
    return (
      <div className='grid grid-cols-2 gap-6 p-4 md:grid-cols-3 lg:grid-cols-4'>
        {Array.from({ length: 8 }).map((_, index) => (
          <div
            key={`skeleton-${index}`}
            className='col-span-1 flex w-full flex-col items-center justify-center gap-2 p-2'
          >
            {/* File icon / thumbnail placeholder */}
            <Skeleton
              width={80}
              height={80}
              borderRadius={8}
              baseColor='#e5e7eb'
              highlightColor='#f3f4f6'
            />

            {/* File name placeholder */}
            <Skeleton
              width={90}
              height={12}
              borderRadius={4}
              baseColor='#e5e7eb'
              highlightColor='#f3f4f6'
            />

            {/* Optional badge / secondary info */}
            <Skeleton
              width={50}
              height={10}
              borderRadius={10}
              baseColor='#e5e7eb'
              highlightColor='#f3f4f6'
            />
          </div>
        ))}
      </div>
    )
  }

  // Empty state
  if (isFetched && items.length === 0) {
    return (
      <div className='col-span-4 flex h-36 w-full flex-col items-center justify-center gap-2'>
        <span className='text-lg font-semibold text-muted-foreground'>
          {translate("Folder is Empty")}
        </span>
        <UploadFile
          projectId={project?.id}
          revalidate={refetch}
          folder={activeFolder}
          mainView={true}
        />
      </div>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <div
        ref={parentRef}
        className='overflow-y-auto'
        style={{ height: containerHeight }}
        onScroll={(e) => {
          const el = e.currentTarget
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 200 && nextPage) {
            handleLoadMoreData()
          }
        }}
      >
        <div className='relative w-full' style={{ height: `${virtualizer.getTotalSize()}px` }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const startIndex = virtualRow.index * COLUMN_COUNT
            const rowItems = items.slice(startIndex, startIndex + COLUMN_COUNT)

            return (
              <div
                key={virtualRow.key}
                className='absolute left-0 top-0 grid w-full grid-cols-2 gap-6 px-4 md:grid-cols-3 lg:grid-cols-4'
                style={{
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {rowItems.map((item, colIndex) => (
                  <GridItem
                    key={`${item.id}_${colIndex}`}
                    item={item}
                    showBatchSelection={showBatchSelection}
                    batchData={batchData}
                    handleSelection={handleSelection}
                    handleClick={handleClick}
                    handleShowTask={handleShowTask}
                    filterActive={filterActive}
                    setClearFilter={setClearFilter}
                    setNewActiveData={setNewActiveData}
                    handleTreeItemFolderClick={handleTreeItemFolderClick}
                    hoveredFolderId={hoveredFolderId}
                  />
                ))}
              </div>
            )
          })}
        </div>
      </div>

      {/* Drag overlay */}
      {createPortal(
        <DragOverlay>
          {activeFileInfinite && (
            <div className='flex flex-col items-center justify-center rounded-lg bg-gray-100 p-4 opacity-80 shadow-xl'>
              <RenderFileIcon size='lg' type={getFileDisplayType(activeFileInfinite)} />
              <span className='w-[80%] truncate text-center text-sm font-medium'>
                {activeFileInfinite?.name}
              </span>
            </div>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  )
}

// --- Need to import useState for DnD state ---
import { useState } from "react"
import Skeleton from "react-loading-skeleton"
import UploadFile from "../../upload-file"
import { IMAGE_EXTENSIONS } from "@pma/utils/constants"
import { useFilesView } from "@pma/hooks/use-files-view"

// --- Individual grid item ---
interface GridItemProps {
  item: DictType
  showBatchSelection: boolean
  batchData: DictType[]
  handleSelection: (item: DictType, event: SyntheticEvent) => void
  handleClick: (item: any) => void
  handleShowTask: (item: any) => React.ReactNode
  filterActive: boolean
  setClearFilter: (val: boolean) => void
  setNewActiveData: (val: any) => void
  handleTreeItemFolderClick: (item: DictType, force?: boolean) => void
  hoveredFolderId: string | null
}

function GridItem({
  item,
  showBatchSelection,
  batchData,
  handleSelection,
  handleClick,
  handleShowTask,
  filterActive,
  setClearFilter,
  setNewActiveData,
  handleTreeItemFolderClick,
  hoveredFolderId,
}: GridItemProps) {
  const isFile = !!item.file

  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef: dragRef,
  } = useDraggable({
    id: isFile ? `file_infinite_${item.id}` : `folder_${item.id}`,
    data: isFile ? { type: "File", file: item } : { type: "Folder", folder: item },
    disabled: !isFile,
  })

  const { setNodeRef: dropRef, isOver } = useDroppable({
    id: `folder_${item.id}`,
    data: { type: "Folder", folder: item },
    disabled: isFile,
  })

  const setNodeRef = isFile ? dragRef : dropRef

  if (isFile) {
    // File item
    return (
      <div
        ref={setNodeRef}
        {...dragAttributes}
        {...dragListeners}
        className={cn(
          "col-span-1 flex w-full flex-col justify-center overflow-hidden",
          batchData.find((val) => item.id === val.id) && "bg-accent",
        )}
      >
        <HoverCard>
          <>
            {showBatchSelection && (
              <input
                name='check'
                type='checkbox'
                id={`check-${item.id}`}
                className='relative h-[17px] w-[17px] self-end'
                checked={!!batchData.find((val) => val.id === item.id)}
                onChange={(event: SyntheticEvent) => handleSelection(item, event)}
                alt='select'
              />
            )}
            <FilePreview
              file={item}
              isSelectionOn={showBatchSelection}
              onClick={() => handleClick(item)}
            >
              <HoverCardTrigger className='flex flex-col items-center justify-center'>
                {IMAGE_EXTENSIONS.includes(item.type) ? (
                  <img
                    src={item?.file}
                    alt='image'
                    className='h-20 w-20 rounded object-cover'
                    loading='lazy'
                  />
                ) : (
                  <RenderFileIcon size='lg' type={getFileDisplayType(item)} />
                )}
                <span
                  className={cn(
                    "w-[80%] truncate text-center text-xs",
                    item.deleted ? "text-red-500" : "",
                  )}
                >
                  {item?.name}
                </span>
                {handleShowTask(item)}
              </HoverCardTrigger>
            </FilePreview>
          </>
          <HoverCardContent className='flex w-fit items-center gap-2'>
            {/* Hover actions are simplified - use FileContextMenu or inline */}
            <span className='text-xs text-muted-foreground'>{item.name}</span>
          </HoverCardContent>
        </HoverCard>
      </div>
    )
  }

  // Folder item
  return (
    <div ref={setNodeRef} className={cn("relative", isOver ? "bg-accent" : "")}>
      <button
        onClick={() => {
          if (!filterActive) handleTreeItemFolderClick(item, true)
          else {
            setClearFilter(true)
            setNewActiveData(item)
          }
        }}
        className='col-span-1 flex w-full flex-col items-center justify-center'
      >
        <div className='relative'>
          <div
            className={`absolute right-0 top-[5px] ${
              item._files_in_folder > 99 ? "h-9 w-10" : "h-8 w-8"
            } align-center flex justify-center rounded-full bg-blue-100 p-1`}
          >
            <span className='p-0 text-center'>
              {item._files_in_folder > 99 ? `+${item._files_in_folder}` : item._files_in_folder}
            </span>
          </div>
          <RenderFileIcon size='lg' type={getFileDisplayType(item)} />
        </div>
        <span
          className={cn("w-[80%] truncate text-center text-xs", item.deleted ? "text-red-500" : "")}
        >
          {item?.name}
        </span>
      </button>
    </div>
  )
}
